import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { privateRoutes } from "./privateRoutes";
import { publicRoutes } from "./publicRoutes";
import { AccessLevelE, UserRoleE } from "../models/enums";
import { useAppSelector } from "../store/hooks";
import { selectCurrentUser } from "../store/selectors/authSelectors";
/**
 * Handles routes based on login state and logged user role
 * */

const NavRoutes = () => {
  const user = useAppSelector(selectCurrentUser);

  const hasAccessRightsOf = (role: UserRoleE | UserRoleE[] | undefined) => {
    // shows for all visitors
    if (!role) return true;

    // check if current user exists
    if (!user) return false;

    // check is role arg array
    if (role instanceof Array) {
      // if so, find at least one match
      return role.some((role) => role === user.role);
    } else {
      return user.role === role;
    }
  };

  /**
   * Second, independent dimension: the user's ACCESS LEVEL within their own client.
   *
   * `role` cannot express this on its own — getUserRole returns the composite client-type string
   * ("AdvertisingAgency_Admin,AdvertisingAgency_User,AdvertisingAgency_Guest"), which is identical
   * for an Admin, a User and a Guest of the same client type.
   *
   * A route that declares no `accessLevel` is unaffected: the early return keeps it on exactly the
   * path it took before this gate existed.
   */
  const hasAccessLevelOf = (accessLevel: AccessLevelE[] | undefined) => {
    if (!accessLevel) return true;

    if (!user) return false;

    return accessLevel.some((level) => level === user.accessLevel);
  };

  return (
    /*@ts-ignore*/
    <Routes>
      {!user
        ? // for unauthorized users
          publicRoutes.map(({ path, element }) => (
            //@ts-ignore
            <Route path={path} element={element} key={path} />
          ))
        : // for logged users
          privateRoutes.map(
            ({ path, element, role, accessLevel, redirectTo }) => {
              // filter available for current user role routes only
              if (!hasAccessRightsOf(role)) return null;

              const allowed = hasAccessLevelOf(accessLevel);

              // Passed `role` but not `accessLevel`, with nowhere declared to send them: leave the
              // route unregistered so it falls through to the `*` entry. Least-privilege default.
              if (!allowed && !redirectTo) return null;

              // Passed `role` but not `accessLevel`: register a redirect INSTEAD of the page, so
              // the page never mounts and never fires its API calls. Dropping the route here would
              // fall through to `*` and land on `/`, not on the intended `redirectTo`.
              // `replace` keeps the blocked URL out of history, so Back does not bounce.
              return (
                //@ts-ignore
                <Route
                  path={path}
                  key={path}
                  element={
                    allowed ? element : <Navigate to={redirectTo!} replace />
                  }
                />
              );
            }
          )}
    </Routes>
  );
};

export default NavRoutes;
